<script lang="ts">
import { defineComponent, reactive, onMounted } from '@vue/composition-api';
import { queryExecutor, commandDispatcher } from '@/_core/cqrs';
import { GetMcpConsentsQuery, McpConsentItem } from '@/api/mcpConsents/queries';
import { RevokeMcpConsentCommand } from '@/api/mcpConsents/commands';

const McpConsents = defineComponent({
    name: 'McpConsents',

    setup() {
        const state = reactive({
            loading: true,
            consents: [] as McpConsentItem[],
            error: null as string | null,
            revoking: null as string | null,
            headers: [
                { text: 'Application', value: 'ClientName' },
                { text: 'Workspace', value: 'WorkspaceName' },
                { text: 'Permissions', value: 'Scopes', sortable: false },
                { text: 'Authorized', value: 'CreatedAt' },
                { text: '', value: 'actions', sortable: false, align: 'end', width: '110px' },
            ],
        });

        const confirmDialog = reactive({
            show: false,
            consentId: '',
            clientName: '',
        });

        const loadConsents = async () => {
            state.loading = true;
            state.error = null;
            try {
                const result = await queryExecutor.executeQuery(new GetMcpConsentsQuery());
                if (result.HasError) {
                    state.error = result.ErrorMessage || 'Failed to load connected applications.';
                    state.consents = [];
                } else {
                    state.consents = result.Consents || [];
                }
            } catch (e) {
                state.error = 'Failed to load connected applications.';
            } finally {
                state.loading = false;
            }
        };

        const openRevokeDialog = (consent: McpConsentItem) => {
            confirmDialog.consentId = consent.AuthorizationId;
            confirmDialog.clientName = consent.ClientName || consent.ClientId;
            confirmDialog.show = true;
        };

        const revokeConsent = async () => {
            const authorizationId = confirmDialog.consentId;
            confirmDialog.show = false;
            state.revoking = authorizationId;
            try {
                const result = await commandDispatcher.dispatchCommand(new RevokeMcpConsentCommand(authorizationId));
                if (result.IsSuccess) {
                    state.consents = state.consents.filter((c) => c.AuthorizationId !== authorizationId);
                } else {
                    state.error = result.Message || 'Failed to revoke consent.';
                }
            } catch (e) {
                state.error = 'Failed to revoke consent.';
            } finally {
                state.revoking = null;
            }
        };

        const formatDate = (dateStr: string | null): string => {
            if (!dateStr) return '-';
            try {
                return new Date(dateStr).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            } catch {
                return dateStr;
            }
        };

        const formatScopes = (scopes: string[]): string => {
            return scopes
                .map((s) => {
                    switch (s) {
                        case 'mcp.read':
                            return 'Read';
                        case 'mcp.write':
                            return 'Write';
                        default:
                            return s;
                    }
                })
                .join(', ');
        };

        onMounted(() => {
            loadConsents();
        });

        return {
            state,
            confirmDialog,
            openRevokeDialog,
            revokeConsent,
            formatDate,
            formatScopes,
            loadConsents,
        };
    },
});

export default McpConsents;
</script>

<template>
  <div class="mcp-consents pa-4">
    <div class="mb-4">
      <h3 class="text-h6 mb-1">
        Connected MCP Applications
      </h3>
      <p class="text-body-2 grey--text">
        These applications have been authorized to access your StoriesOnBoard data via
        the Model Context Protocol (MCP).
      </p>
    </div>

    <v-alert
      v-if="state.error"
      type="error"
      dense
      dismissible
      class="mb-4"
      @input="state.error = null"
    >
      {{ state.error }}
    </v-alert>

    <v-data-table
      :headers="state.headers"
      :items="state.consents"
      :loading="state.loading"
      item-key="AuthorizationId"
      sort-by="CreatedAt"
      :sort-desc="true"
      fixed-header
      height="calc(80vh - 250px)"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      class="elevation-1"
    >
      <template #item.ClientName="{ item }">
        {{ item.ClientName || item.ClientId }}
      </template>

      <template #item.WorkspaceName="{ item }">
        {{ item.WorkspaceName || '-' }}
      </template>

      <template #item.Scopes="{ item }">
        {{ formatScopes(item.Scopes) }}
      </template>

      <template #item.CreatedAt="{ item }">
        {{ formatDate(item.CreatedAt) }}
      </template>

      <template #item.actions="{ item }">
        <v-btn
          text
          color="error"
          :loading="state.revoking === item.AuthorizationId"
          :disabled="state.revoking !== null"
          @click="openRevokeDialog(item)"
        >
          Revoke
        </v-btn>
      </template>

      <template #no-data>
        <div class="text-center py-8">
          <v-icon
            size="48"
            color="grey lighten-1"
            class="mb-3"
          >
            mdi-shield-check-outline
          </v-icon>
          <p class="text-body-1 grey--text mb-0">
            No connected applications.
          </p>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="confirmDialog.show"
      max-width="420"
    >
      <v-card>
        <v-card-title>Revoke Access</v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-3">
            Revoke access for <strong>{{ confirmDialog.clientName }}</strong>?
          </p>
          <p class="mb-0">
            This application will no longer be able to access your data. You can re-authorize it later.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmDialog.show = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            text
            @click="revokeConsent"
          >
            Revoke
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
